import {Address, Contact, NotificationType, PaymentType, PickUp} from "../../API";
import {createSlice, PayloadAction} from "redux-starter-kit";
import {isMockActive, nextWorkDayInRange} from "./utils";
import {initialContactMockState} from "./mockState";
import {MomentObjectOutput} from "moment";

export type ContactState =
    Omit<Contact, "birthday" | "pickUp">
    & { birthday: MomentObjectOutput | undefined, pickUp: PickUpState | null; };

export type PickUpState = Omit<PickUp, "date"> & { date: MomentObjectOutput | undefined; };

const initialContactState: ContactState = {
    address: {
        name: "",
        lastName: "",
        street: "",
        postalCode: "",
        city: "",
        country: "0"
    },
    birthday: undefined,
    email: "",
    phone: "",
    notificationType: [NotificationType.EMAIL],
    paymentType: PaymentType.TRANSFER,
    pickUp: null
};

const PICK_UP_WEIGHT = {
    initial: 0.5,
    changeDifference: 0.5
};

export const initialPickUpState: PickUpState = {
    address: {
        name: "",
        lastName: "",
        street: "",
        postalCode: "",
        city: "",
        country: ""
    },
    weight: PICK_UP_WEIGHT.initial,
    date: nextWorkDayInRange(2).toObject(),
    timespan: "0"
};

const contactSlice = createSlice({
    slice: "contact",
    initialState: isMockActive() ? initialContactMockState : initialContactState,
    reducers: {
        setName(state, action: PayloadAction<string>) {
            state.address.name = action.payload;
        },
        setLastName(state, action: PayloadAction<string>) {
            state.address.lastName = action.payload;
        },
        setStreet(state, action: PayloadAction<string>) {
            state.address.street = action.payload;
        },
        setPostalCode(state, action: PayloadAction<string>) {
            state.address.postalCode = action.payload;
        },
        setCity(state, action: PayloadAction<string>) {
            state.address.city = action.payload;
        },
        setCountry(state, action: PayloadAction<string>) {
            state.address.country = action.payload;
        },
        setBirthday(state, action: PayloadAction<MomentObjectOutput | undefined>) {
            state.birthday = action.payload;
        },
        setEmail(state, action: PayloadAction<string>) {
            state.email = action.payload;
        },
        setPhone(state, action: PayloadAction<string>) {
            state.phone = action.payload;
        },
        toggleNotificationType(state, action: PayloadAction<NotificationType>) {
            if (state.notificationType.includes(action.payload)) {
                if (state.notificationType.length > 1) {
                    state.notificationType = state.notificationType.filter(item => item !== action.payload);
                }
            } else {
                state.notificationType = [...state.notificationType, action.payload];
            }
        },
        setPaymentType(state, action: PayloadAction<PaymentType>) {
            state.paymentType = action.payload;
        },
        setPickUpAddress(state, action: PayloadAction<Address | null>) {
            if (!state.pickUp) state.pickUp = initialPickUpState;
            state.pickUp.address = action.payload;
        },
        incrementPickUpWeight(state) {
            if (!state.pickUp) state.pickUp = initialPickUpState;
            state.pickUp.weight = state.pickUp.weight + PICK_UP_WEIGHT.changeDifference;
        },
        decrementPickUpWeight(state) {
            if (!state.pickUp) state.pickUp = initialPickUpState;
            if (state.pickUp.weight > PICK_UP_WEIGHT.initial) {
                state.pickUp.weight = state.pickUp.weight - PICK_UP_WEIGHT.changeDifference;
            }
        },
        setPickUpDate(state, action: PayloadAction<MomentObjectOutput | undefined>) {
            if (!state.pickUp) state.pickUp = initialPickUpState;
            state.pickUp.date = action.payload;
        },
        setPickUpTimespan(state, action: PayloadAction<string>) {
            if (!state.pickUp) state.pickUp = initialPickUpState;
            state.pickUp.timespan = action.payload;
        },
        removePickUp(state) {
            state.pickUp = null;
        }
    }
});

export const {
    setEmail,
    setPhone,
    toggleNotificationType,
    setBirthday,
    setPickUpAddress,
    setName,
    setCity,
    setCountry,
    setLastName,
    setPostalCode,
    setStreet,
    setPickUpDate,
    setPickUpTimespan,
    incrementPickUpWeight,
    decrementPickUpWeight,
    setPaymentType,
    removePickUp
} = contactSlice.actions;

export default contactSlice.reducer;
